'use client';

import { useState } from 'react';
import { cn } from '@/utils/cn';
import PdfDocumentCard, { DocumentGrid } from '@/components/ui/PdfDocumentCard';
import type { ComplianceTab } from '@/types/compliance';

interface ComplianceTabsProps {
  tabs: ComplianceTab[];
}

export default function ComplianceTabs({ tabs }: ComplianceTabsProps) {
  const [activeId, setActiveId] = useState<number>(tabs[0]?.id ?? -1);
  const active = tabs.find((tab) => tab.id === activeId) ?? tabs[0];

  if (!active) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[#e4e4e4] bg-white shadow-sm">
      <div
        role="tablist"
        aria-label="Compliance categories"
        className="flex overflow-x-auto border-b border-[#e4e4e4] bg-[#faf9f7]"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === active.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`compliance-tab-${tab.id}`}
              id={`compliance-tab-trigger-${tab.id}`}
              onClick={() => setActiveId(tab.id)}
              className={cn(
                'relative shrink-0 px-5 py-4 text-center text-sm font-medium transition-colors sm:px-6 sm:py-5 sm:text-base',
                isActive
                  ? 'bg-white text-[#1C1917]'
                  : 'text-[#737373] hover:bg-white/70 hover:text-[#1C1917]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#ed1c25]/50',
              )}
            >
              {tab.tabTitle}
              {isActive ? (
                <span
                  aria-hidden="true"
                  className="absolute inset-x-4 bottom-0 h-0.5 rounded-full bg-[#ed1c25] sm:inset-x-6"
                />
              ) : null}
            </button>
          );
        })}
      </div>

      <div
        id={`compliance-tab-${active.id}`}
        role="tabpanel"
        aria-labelledby={`compliance-tab-trigger-${active.id}`}
        className="p-5 sm:p-6 md:p-8"
      >
        {active.tabContent.map((group, idx) => (
          <div key={group.id} className={idx > 0 ? 'mt-10 border-t border-[#f0f0f0] pt-10' : ''}>
            {group.heading ? (
              <h2 className="mb-5 font-figtree text-lg font-semibold tracking-tight text-[#1C1917] sm:mb-6 sm:text-xl">
                {group.heading}
              </h2>
            ) : null}
            {group.item.length > 0 ? (
              <DocumentGrid>
                {group.item.map((item) => (
                  <PdfDocumentCard
                    key={item.id}
                    item={{
                      id: item.id,
                      title: item.title,
                      description: item.descriptionOptional,
                      href: item.uploadPdf?.url ?? null,
                    }}
                  />
                ))}
              </DocumentGrid>
            ) : (
              <p className="rounded-xl bg-[#faf9f7] px-4 py-6 text-center text-sm text-[#737373]">
                No documents available in this section.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
